import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SettingsService } from '../../service/settings.service';
import { deepCopy, typesOptions, listTypesOptions, validatorsOptions, contentTypeSchemaValidatorTextFormat } from 'na-lib';
import { CommentsService } from '../../service/comments.service';
import { TranslateService } from '@ngx-translate/core';
import { ContentTypesSchemaValidatorDialogComponent } from '../content-types-schema-validator-dialog/content-types-schema-validator-dialog.component';
import { EMPTY_SCHEMA_META_TEMPLATE, getMissingMandatoryMetaFields, SchemaFieldMeta, SchemaMetaTemplate } from '../schema-meta.model';

@Component({
    selector: 'app-content-types-schema-properties-dialog',
    templateUrl: './content-types-schema-properties-dialog.component.html',
    styleUrls: ['./content-types-schema-properties-dialog.component.css'],
    standalone: false
})
export class ContentTypesSchemaPropertiesDialogComponent {
  schemaPropertyForm!:FormGroup
  schemaPropertyData:any
  typesOptions:any = typesOptions

  listTypesOptions:any = listTypesOptions

  validatorsOptions: any = validatorsOptions

  format:any = contentTypeSchemaValidatorTextFormat

  textFormatOptions: any = []
  allValidators: any = []
  rulesExpanded = false;
  metadataExpanded = false;
  metaTemplate: SchemaMetaTemplate | undefined;
  fieldMeta: SchemaFieldMeta = {};
  showMetaValidationErrors = false;
  accessExpanded = false;
  accessEnabled = false;
  inheritedAccessFrom: string | undefined;
  fieldAccess: Record<string, string[]> = {};
  newAccessRole = '';
  readonly accessPermissions = ['C', 'R', 'U', 'D'] as const;

  get accessRoles(): string[] {
    return ['public', ...Object.keys(this.fieldAccess).filter(role => role !== 'public')];
  }

  get accessRoleErrorKey(): string | null {
    const role = this.newAccessRole.trim();
    if (!role) return null;
    if (!/^[A-Za-z][A-Za-z0-9_-]*$/.test(role) || ['public', '__proto__', 'constructor', 'prototype'].includes(role)) {
      return 'contentTypes.fieldAccessInvalidRole';
    }
    if (Object.prototype.hasOwnProperty.call(this.fieldAccess, role)) return 'contentTypes.fieldAccessDuplicateRole';
    return null;
  }

  setAccessEnabled(enabled: boolean): void {
    if (enabled && !this.accessEnabled && this.inheritedAccessFrom) return;
    this.accessEnabled = enabled;
    if (enabled) {
      if (!Object.keys(this.fieldAccess).length) this.fieldAccess = { public: ['R'] };
      this.accessExpanded = true;
    }
  }

  hasAccessPermission(role: string, permission: string): boolean {
    const permissions = Object.prototype.hasOwnProperty.call(this.fieldAccess, role) ? this.fieldAccess[role] : undefined;
    return Array.isArray(permissions) && permissions.includes(permission);
  }

  setAccessPermission(role: string, permission: string, enabled: boolean): void {
    if (role === 'public' && permission !== 'R') return;
    const permissions = this.fieldAccess[role] || [];
    this.fieldAccess = {
      ...this.fieldAccess,
      [role]: enabled ? [...new Set([...permissions, permission])] : permissions.filter(value => value !== permission)
    };
  }

  addAccessRole(): void {
    const role = this.newAccessRole.trim();
    if (!role || this.accessRoleErrorKey) return;
    this.fieldAccess = { ...this.fieldAccess, [role]: [] };
    this.newAccessRole = '';
  }

  removeAccessRole(role: string): void {
    if (role === 'public') return;
    const access = { ...this.fieldAccess };
    delete access[role];
    this.fieldAccess = access;
  }

  schemaTypeLabel(type: string | undefined): string {
    const translationKeys: Record<string, string> = {
      string: 'text',
      number: 'number',
      boolean: 'boolean',
      array: 'list',
      object: 'key_value',
      date: 'date'
    };
    const key = translationKeys[type || ''];
    return key ? this.translate.instant(`contentTypes.${key}`) : type || '';
  }

  constructor(public dialogRef: MatDialogRef<ContentTypesSchemaPropertiesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
            private fb: FormBuilder,
    public dialog: MatDialog,
    private settingsService:SettingsService,
    private commentsService: CommentsService,
    private translate:TranslateService ){
      dialogRef.disableClose = true;
  }

  ngOnInit(){
    const {type, key, description, dateFormat, mandatory, listItemsType, properties, items, validators, meta, access} = this.data.data ?? {}
    this.metaTemplate = this.data.metaTemplate;
    this.inheritedAccessFrom = this.data.inheritedAccessFrom;
    this.fieldMeta = deepCopy(meta || {});
    this.metadataExpanded = !!this.metaTemplate?.fields?.length;
    this.accessEnabled = access !== undefined && access !== null;
    this.accessExpanded = this.accessEnabled || !!this.inheritedAccessFrom;
    this.fieldAccess = deepCopy(access || {});
    if (this.accessEnabled && !Object.prototype.hasOwnProperty.call(this.fieldAccess, 'public')) this.fieldAccess = { public: [], ...this.fieldAccess };
    if (this.accessEnabled) this.fieldAccess['public'] = Array.isArray(this.fieldAccess['public']) && this.fieldAccess['public'].includes('R') ? ['R'] : [];

    this.allValidators = validators ? validators.filter(v => v.type != 'mandatory') : (type === 'array' ? [] : items?.validators || [])
    this.rulesExpanded = this.allValidators.length > 0;

    this.schemaPropertyForm=this.fb.group({
      type:[{value: type ? type : undefined, disabled:false},Validators.required],
      key:[{value: key ? key : undefined, disabled:false},[Validators.required,Validators.pattern('^(?![_\\.])(?!.*[_\\.]$)(?!.*[\'\";,#%&<>(){}\\[\\]/\\\\|`*@$^=+?!]).*$')]],
      description:[{value: description ? description : undefined, disabled:false}],
      dateFormat:[{value: dateFormat ? dateFormat : undefined, disabled:false}],
      mandatory:[{value: mandatory === true, disabled:false}],
      listItemsType:[{value: listItemsType ? listItemsType : (items?.type ? items.type: undefined), disabled:false}],
      properties:[{value: properties ? properties : undefined, disabled:false}],
      items:[{value: items? items : undefined, disabled:false}],
      validators: [{value: validators ? validators : (type === 'array' ? [] : items?.validators), disabled: false}]
    })

    this.schemaPropertyForm.get("key")?.valueChanges.subscribe( newkey =>{
      if(newkey.includes(" ")){
        newkey = newkey.replaceAll(" ", "_")
        this.schemaPropertyForm.get("key")?.setValue( newkey)
      }
    })
    this.schemaPropertyForm.get("validators")?.valueChanges.subscribe( v => {
      this.allValidators = v || []
    })

    this.schemaPropertyForm.get("type")?.valueChanges.subscribe( v => {
      this.allValidators = []
      this.schemaPropertyForm.get("validators")?.setValue(this.allValidators)
    })
  }

  addValidator(data){
    let dialogRef= this.dialog.open(ContentTypesSchemaValidatorDialogComponent, {
      data: {
        mode:data.type,
        data: { type: data.type },
      },
    });
  
    dialogRef.afterClosed().subscribe(result => {
      if(result?.[0]){
        const allv = deepCopy(this.allValidators)
        allv.push(result[1])
        this.schemaPropertyForm.patchValue({validators: allv})
      }
    });
  }

  openValidatorDialog(data, index, type) {
    let dialogRef= this.dialog.open(ContentTypesSchemaValidatorDialogComponent, {
      data: {
        mode:type,
        data,
      },
    });
  
    dialogRef.afterClosed().subscribe(result => {
      if(result?.[0]){
        const allv = deepCopy(this.allValidators)
        allv[index] = this.removeOnlyUndefined(result[1])
        this.schemaPropertyForm.patchValue({validators: allv})
      }
    });
    
  }

  checkDisabledType(val) {
    return (this.allValidators || []).some(ty => ty.type === val.type)
  }

  getAvailableValidators(): any[] {
    const type = this.schemaPropertyForm?.value?.type;
    if (type === 'array') return [{ type: 'arrayLength', only: ['array'] }];
    return (this.validatorsOptions || []).filter(val => val?.only?.includes(type));
  }

  getValidatorIcon(type: string): string {
    const icons: Record<string, string> = {
      textLength: 'format_size',
      textFormat: 'format_align_justify',
      textRegex: 'code',
      textEnum: 'list_alt',
      numericRange: 'numbers',
      arrayLength: 'format_list_numbered',
      numericEnum: 'format_list_numbered',
      textEMail: 'mail',
      dateRange: 'date_range'
    };
    return icons[type] || 'rule';
  }

  getValidatorLabel(type: string): string {
    const key = `contentTypes.schemaRuleLabels.${type}`;
    const label = this.translate.instant(key);
    return label === key ? this.translate.instant('contentTypes.schemaRuleLabels.default') : label;
  }

  getValidatorSummary(validator: any): string {
    if (!validator) return this.translate.instant('contentTypes.schemaRuleSummary.noDetails');
    if (validator.type === 'textLength') return this.formatRangeSummary(this.translate.instant('contentTypes.schemaRuleSummary.characters'), validator.min, validator.max);
    if (validator.type === 'numericRange') {
      const range = this.formatRangeSummary(this.translate.instant('contentTypes.schemaRuleSummary.value'), validator.min, validator.max);
      if (validator.decimal !== false) return range;
      const wholeNumbers = this.translate.instant('contentTypes.schemaRuleSummary.wholeNumbers');
      return validator.min == null && validator.max == null ? wholeNumbers : `${range} ${wholeNumbers}`;
    }
    if (validator.type === 'arrayLength') return this.formatRangeSummary(this.translate.instant('contentTypes.schemaRuleSummary.items'), validator.min, validator.max);
    if (validator.type === 'dateRange') return this.formatRangeSummary(this.translate.instant('contentTypes.schemaRuleSummary.days'), validator.min, validator.max);
    if (validator.type === 'textFormat') return validator.format
      ? this.translate.instant('contentTypes.schemaRuleSummary.formatRequired', { format: validator.format })
      : this.translate.instant('contentTypes.schemaRuleSummary.formatExpected');
    if (validator.type === 'textRegex') return validator.hint || validator.regex || this.translate.instant('contentTypes.schemaRuleSummary.patternRequired');
    if (validator.type === 'textEnum' || validator.type === 'numericEnum') return this.formatEnumSummary(validator.values);
    if (validator.type === 'textEMail') return validator.lowercase
      ? this.translate.instant('contentTypes.schemaRuleSummary.emailLowercase')
      : this.translate.instant('contentTypes.schemaRuleSummary.emailRequired');
    return this.translate.instant('contentTypes.schemaRuleSummary.restricted');
  }

  private formatRangeSummary(label: string, min: any, max: any): string {
    const hasMin = min !== undefined && min !== null && min !== '';
    const hasMax = max !== undefined && max !== null && max !== '';
    if (hasMin && hasMax) return this.translate.instant('contentTypes.schemaRuleSummary.range', { label, min, max });
    if (hasMin) return this.translate.instant('contentTypes.schemaRuleSummary.minimum', { label, min });
    if (hasMax) return this.translate.instant('contentTypes.schemaRuleSummary.maximum', { label, max });
    return this.translate.instant('contentTypes.schemaRuleSummary.setRange');
  }

  private formatEnumSummary(values: any): string {
    const list = Array.isArray(values) ? values : [];
    if (!list.length) return this.translate.instant('contentTypes.schemaRuleSummary.setAllowedValues');
    if (list.length <= 4) return this.translate.instant('contentTypes.schemaRuleSummary.allowedValues', { values: list.join(', ') });
    return this.translate.instant('contentTypes.schemaRuleSummary.allowedValuesCount', { count: list.length });
  }

  deleteValidator(v, index) {
    const allv = deepCopy(this.allValidators)
    allv.splice(index, 1);
    this.schemaPropertyForm.patchValue({validators: allv})
  }

  onCancelEdit(){
    this.dialogRef.close([false, this.schemaPropertyForm.value])
  }

  onSaveEdit(){
    if(this.schemaPropertyForm.valid){
      const {type, key, description, mandatory, dateFormat, validators, listItemsType, properties, items} = this.schemaPropertyForm.value
      const missingMetaFields = getMissingMandatoryMetaFields(
        [{ key, meta: this.fieldMeta }],
        this.metaTemplate || EMPTY_SCHEMA_META_TEMPLATE
      );
      if (missingMetaFields.length) {
        this.showMetaValidationErrors = true;
        this.metadataExpanded = true;
        this.commentsService.addSnackBar.emit(this.translate.instant('contentTypes.mandatoryMetaMissing', {
          fields: missingMetaFields.join(', ')
        }));
        return;
      }

      const values = {
        type: type ? type : undefined,
        key: key ? key : undefined, 
        description: description ? description : undefined,
        dateFormat: dateFormat ? dateFormat : undefined,
        mandatory: mandatory !== undefined ? mandatory : undefined,
        listItemsType: listItemsType ? listItemsType : undefined,
        properties: properties ? properties : undefined,
        items: items ? items : undefined,
        validators: validators ? validators : undefined
      }

      if(type === "object"){
        if(items)
        items.validators = this.allValidators
        delete values.validators
      }

      if(type=='object'){
        values.properties=properties || []
        delete values.items
      }
      if(type === 'array' && listItemsType){
        delete values['listItemsType']
        values['items'] = {
          ...(items?.type === listItemsType ? items : {}),
          key: items?.key || 'items',
          type: listItemsType
        }
        if (listItemsType === 'object') values['items'].properties = items?.type === 'object' ? items.properties || [] : [];
        else delete values['items'].properties;
        delete values.properties;
      }

      if(!validators || validators.length === 0)
        delete values.validators
      const data = this.removeOnlyUndefined(values)
      const meta = Object.keys(this.fieldMeta).length ? this.fieldMeta : undefined;
      this.dialogRef.close([true, data, meta, this.accessEnabled ? deepCopy(this.fieldAccess) : null])
    }else{
      const message = this.translate.instant("SnackBarConstants.MANDATORY_FIELDS_EMPTY")
      this.commentsService.addSnackBar.emit(message)
    }
  }

  updateFieldMeta(meta: SchemaFieldMeta | undefined): void {
    this.fieldMeta = deepCopy(meta || {});
  }

  private removeOnlyUndefined(values: Record<string, any>): Record<string, any> {
    return Object.fromEntries(Object.entries(values).filter(([, value]) => value !== undefined));
  }
}
